import { useTranslation } from 'react-i18next';
import PrimarySquareBtn from '../../../components/buttons/PrimarySquareBtn';
import { Download, Key, Save2, Sliders2, X, XCircle, XSquare } from 'react-bootstrap-icons';
import SecondarySquareBtn from '../../../components/buttons/SecondarySquareBtn';
import { Button, Col, DatePicker, Form, Row, Select, Spin, Table } from 'antd';
import TableComponent from '../../../components/common/TableComponent';
import { SetStateAction, useCallback, useEffect, useState } from 'react';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { getGeneratedReport, getPriceVsVarianceGraphData } from '../getData';
import dayjs, { Dayjs } from 'dayjs';
import PriceVsVarianceChart from './PriceVsVarianceChart_apex';
import NewsLetterTable from './NewsLetterTable';
import TextArea from 'antd/es/input/TextArea';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import { SaveOutlined } from '@ant-design/icons';
import FormModelComponent from '../../../components/model/FormModelComponent';
import { useForm } from 'antd/es/form/Form';
import AddNewsLetterForm from './AddNewsLetterForm';
import ConfirmModelComponent from '../../../components/model/ConfirmationModelComponent';
import { trimValues } from '../../../utils/trimValues';
import { createNewsletter } from '../../../api/services/newsLetterService';
import { normalize } from '../../../utils/normalizeWords';
import { disableFutureDates } from '../../../utils/disableDates';
import { REGEX_ALL_ALLOWED_SAFE_FILTER } from '../../../constants/regex';

type TableData = {
  // key: string;
  // product: string;
  [key: string]: string | number;
};

export interface INewsLetterGenerationProps {
  handleUpdateNewBtn: () => void;
}

const CustomNewsletterGeneration: React.FC<INewsLetterGenerationProps> = ({
  handleUpdateNewBtn,
}) => {
  const { t } = useTranslation(['newsletter', 'basket']);

  const [selectedBasket, setSelectedBasket] = useState<number | undefined>(undefined);
  const [baskets, setBaskets] = useState<any[]>([]);

  const [isLoadingFilterData, setIsLoadingFilterData] = useState<boolean>(false);

  const { successNotification, errorNotification } = useNotificationContext();

  const [establishmentTypes, setEstablishmentTypes] = useState<{label: string, value: string}[]>([]);
  const [establishments, setEstablishments] = useState<string[]>([]);
  const [selectedEstablishmentType, setSelectedEstablishmentType] = useState<string[] | undefined>(
    undefined
  );
  const [selectedEstablishments, setSelectedEstablishments] = useState<string[]>([]);
  const [selectedEstablishmentIds, setSelectedEstablishmentIds] = useState<number[]>([]);
  const [disableSaveNewsletter, setDisableSaveNewsletter] = useState<boolean>(false);

  const [establishmentData, setEstablishmentData] = useState<
    Array<{ positionType: string; name: string; id: number }>
  >([]);

  const [selectedStartDate, setSelectedStartDate] = useState<any>();
  const [selectedEndDate, setSelectedEndDate] = useState<any>();

  const [enableClearFiltersBtn, setEnableClearFiltersBtn] = useState<boolean>(false);
  const [enableGenerateReportBtn, setEnableGenerateReportBtn] = useState<boolean>(false);
  const [enableDownloadBtn, setEnableDownloadBtn] = useState<boolean>(false);
  const [selectedFilters, setSelectedFilters] = useState({});

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoadingGeneratedData, setIsLoadingGeneratedData] = useState<boolean>(false);
  const [isLoadingGraphData, setIsLoadingGraphData] = useState<boolean>(false);

  const [pageSize] = useState<number>(100);
  const [generatedData, setGeneratedData] = useState([]);
  const [generatedGraphData, setGeneratedGraphData] = useState([]);

  const [totalGeneratedData, setTotalGeneratedData] = useState<number>(0);

  const [dynamicTableColumns, setDynamicTableColumns] = useState<any[]>([]);

  const [generatedTableData, setGeneratedTableData] = useState<TableData[]>([]);

  const [isNewsLetterModalOpen, setIsNewsLetterModalOpen] = useState<boolean>(false);

  const [newNewsLetter, setNewNewsLetter] = useState({});
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);
  const [addNewsLetterForm] = useForm();
  const [descriptionForm] = useForm();

  const [filterParams, setFilterParams] = useState({});

  const [newsletterFiltersForm] = useForm();

  const getGeneratedData = async (page: number = currentPage, filters?: any) => {
    // setIsLoadingGeneratedData(true);
    // try {
    const paginationParams = {
      page,
      limit: pageSize,
    };

    const filterParams: any = {
      // ...paginationParams,
    };

    if (filters?.basketId) {
      filterParams.basketId = filters?.basketId;
    }

    if (filters?.startDate) {
      filterParams.startDate = dayjs(filters?.startDate).valueOf();
    }

    if (filters?.endDate) {
      filterParams.endDate = dayjs(filters?.endDate).valueOf();
    }

    if (
      filters?.establishmentIds &&
      Array.isArray(filters?.establishmentIds) &&
      filters?.establishmentIds.length > 0
    ) {
      filterParams.establishmentIds = filters?.establishmentIds;
    }

    filterParams.timezoneOffset = filters?.timezoneOffset;

    setFilterParams(filterParams);
    // get table data
    getNewsletterTableData(filterParams);

    // get graph data
    getNewsletterGraphData(filterParams);
  };

  const getNewsletterTableData = async (filterParams: any) => {
    setIsLoadingGeneratedData(true);
    try {
      // get table data
      const response = await getGeneratedReport(filterParams);

      if (response.statusText === 'SUCCESS') {
        const reportData = response.data?.data || response.data || [];
        setGeneratedData(reportData);
      } else {
        errorNotification(t('tableFetchError'));
      }
    } catch (error) {
      console.error('Failed to fetch table from backend:', error);
      errorNotification(t('tableFetchError'));
    } finally {
      setIsLoadingGeneratedData(false);
    }
  };

  const getNewsletterGraphData = async (filterParams: any) => {
    setIsLoadingGraphData(true);
    try {
      // get graph data
      const response = await getPriceVsVarianceGraphData(filterParams);

      if (response.statusText === 'SUCCESS') {
        const graphData = response.data?.data || response.data || [];

        setGeneratedGraphData(graphData);
      } else {
        errorNotification(t('graphFetchError'));
      }
    } catch (error) {
      console.error('Failed to fetch graph data from backend:', error);
      errorNotification(t('graphFetchError'));
    } finally {
      setIsLoadingGraphData(false);
    }
  };

  useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchFilterData = useCallback(async () => {
    setIsLoadingFilterData(true);
    try {
      const basketsData = await referenceDataSyncService.getBaskets();
      setBaskets(basketsData);

      const establishmentData = await referenceDataSyncService.getEstablishments();
      const formattedEstablishmentData = establishmentData.map((establishment: any) => ({
        positionType: (establishment.establishmentType?.key || establishment.establishmentType?.nameEn || establishment.establishmentType || '') as string,
        name: establishment.name as string,
        id: establishment.id as number,
      }));
      setEstablishmentData(formattedEstablishmentData);
      setEstablishments(formattedEstablishmentData);
      
      const uniqueTypesMap = new Map<string, string>();
      establishmentData.forEach((est: any) => {
        const typeObj = est.establishmentType;
        if (typeObj && typeof typeObj === 'object' && typeObj.key) {
          uniqueTypesMap.set(typeObj.key, typeObj.nameEn || typeObj.key);
        } else if (typeof typeObj === 'string' && typeObj) {
          uniqueTypesMap.set(typeObj, typeObj);
        }
      });
      setEstablishmentTypes(
        Array.from(uniqueTypesMap.entries()).map(([key, name]) => ({
          label: name,
          value: key,
        }))
      );
    } catch (error) {
      console.error('Error fetching filter data:', error);
    } finally {
      setIsLoadingFilterData(false);
    }
  }, [errorNotification]);

  useEffect(() => {
    if (selectedEstablishmentType) {
      const establishmentsForSelectedType = establishmentData
        .filter((establishment) => selectedEstablishmentType.includes(establishment?.positionType))
        .map((establishment) => establishment.id);

      setSelectedEstablishmentIds(establishmentsForSelectedType);

      const filteredEstablishments = establishmentData
        .filter((establishment) => selectedEstablishmentType.includes(establishment?.positionType))
        .map((establishment) => establishment.name);

      const establishmentWithoutDuplicates = Array.from(new Set(filteredEstablishments));

      setEstablishments(establishmentWithoutDuplicates);
      setSelectedEstablishments([]);
    } else {
      setEstablishments([]);
      setSelectedEstablishments([]);
      setSelectedEstablishmentIds([]);
    }
  }, [selectedEstablishmentType, establishmentData]);

  useEffect(() => {
    clearTableGraphData();

    const filters = {
      basketId: selectedBasket,
      establishmentIds: selectedEstablishmentIds,
      startDate: new Date(selectedStartDate).getTime(),
      endDate: new Date(selectedEndDate).getTime(),
      timezoneOffset: new Date().getTimezoneOffset(),
    };

    const hasFilters =
      selectedBasket !== undefined ||
      selectedStartDate !== undefined ||
      selectedEndDate !== undefined ||
      selectedEstablishmentIds.length > 0;

    const hasAllRequiredFilters =
      selectedBasket !== undefined &&
      selectedStartDate !== undefined &&
      selectedStartDate !== null &&
      selectedEndDate !== undefined &&
      selectedEndDate !== null &&
      selectedEstablishments.length > 0;

    if (hasFilters) {
      // getGeneratedData(1, filters);
      setEnableClearFiltersBtn(true);
    } else {
      // getGeneratedData(1, {});
      setEnableClearFiltersBtn(false);
    }

    enableDisableBtns(hasAllRequiredFilters, filters);
  }, [selectedBasket, selectedStartDate, selectedEndDate, selectedEstablishmentIds]);

  const enableDisableBtns = async (hasAllRequiredFilters: boolean, filters: any) => {
    if (hasAllRequiredFilters) {
      const isValid = await newsletterFiltersForm.validateFields();
      setEnableGenerateReportBtn(true);
      setSelectedFilters(filters);
    } else {
      setEnableGenerateReportBtn(false);
      setEnableDownloadBtn(false);
      setSelectedFilters({});
      clearTableGraphData();
    }
  };

  const clearTableGraphData = () => {
    setGeneratedData([]);
    setGeneratedGraphData([]);
    descriptionForm.resetFields();
  };

  useEffect(() => {
    if (selectedEstablishments && selectedEstablishmentType) {
      const establishmentIdsForSelectedEstablishment = establishmentData
        .filter(
          (establishment) =>
            selectedEstablishmentType.includes(establishment.positionType) &&
            selectedEstablishments.includes(establishment.name)
        )
        .map((establishment) => establishment.id);

      setSelectedEstablishmentIds(establishmentIdsForSelectedEstablishment);
    } else if (selectedEstablishments) {
      const establishmentIdsForSelectedEstablishment = establishmentData
        .filter((establishment) => selectedEstablishments.includes(establishment.name))
        .map((establishment) => establishment.id);

      setSelectedEstablishmentIds(establishmentIdsForSelectedEstablishment);
    } else if (selectedEstablishmentType) {
      const establishmentIdsForSelectedType = establishmentData
        .filter((establishment) => selectedEstablishmentType.includes(establishment.positionType))
        .map((establishment) => establishment.id);

      setSelectedEstablishmentIds(establishmentIdsForSelectedType);
    } else {
      setSelectedEstablishmentIds([]);
    }
  }, [selectedEstablishments]);

  const clearAllFilters = () => {
    setSelectedBasket(undefined);
    setSelectedEstablishmentType(undefined);
    setSelectedEstablishments([]);
    setSelectedEstablishmentIds([]);
    setSelectedStartDate(undefined);
    setSelectedEndDate(undefined);
    newsletterFiltersForm.resetFields();
    descriptionForm.resetFields();

    setEnableGenerateReportBtn(false);
    setEnableDownloadBtn(false);
    setSelectedFilters({});
    clearTableGraphData();
  };

  const handleGenerateReport = () => {
    getGeneratedData(1, selectedFilters);
  };

  const saveNewsLetterData = async () => {
    try {
      await descriptionForm.validateFields();
      setIsNewsLetterModalOpen(true);
    } catch (errorInfo) {}
  };

  const closeNewsLetterModal = () => {
    addNewsLetterForm.resetFields();
    setNewNewsLetter({});
    setIsNewsLetterModalOpen(false);
  };

  const handleCancel = () => {
    addNewsLetterForm.resetFields();
    setNewNewsLetter({});
    setIsNewsLetterModalOpen(false);
  };

  const handleNewsLetterAddition = async () => {
    setDisableSaveNewsletter(true);
    let titleModalData = addNewsLetterForm.getFieldsValue();

    const trimmedValues = trimValues(titleModalData);
    addNewsLetterForm.setFieldsValue(trimmedValues);
    await addNewsLetterForm.validateFields();

    const descriptionValues = descriptionForm.getFieldsValue();
    const establishmentDesc: { [key: string]: string } = {};
    for (const key in descriptionValues) {
      if (key.startsWith('establishment-desc-')) {
        const id = key.replace('establishment-desc-', '');
        if (descriptionValues[key]) {
          establishmentDesc[id] = descriptionValues[key];
        }
      }
    }

    const body = {
      title: trimmedValues?.title,
      summary: trimmedValues?.summary,
      data: {
        filters: filterParams,
        tableDesc: descriptionValues?.mainDescription,
        establishmentDesc: establishmentDesc,
      },
      published: false,
    };

    try {
      const result = await createNewsletter(body);
      successNotification(t('newsletterCreatedSuccess'));
      handleUpdateNewBtn();
    } catch (error) {
      errorNotification(t('newsletterCreatedFail'));
    } finally {
      setDisableSaveNewsletter(false);
    }
    setIsConfirmationOpen(false);
    setIsNewsLetterModalOpen(false);
  };

  const getConfirmation = async () => {
    await addNewsLetterForm.validateFields();
    // if (generatedData?.length > 0 || generatedGraphData?.length > 0) {
    //   await descriptionForm.validateFields();
    // }
    setIsConfirmationOpen(true);
  };

  const cancelConfirmation = () => {
    setIsConfirmationOpen(false);
  };

  return (
    <>
      <ConfirmModelComponent
        isOpen={isConfirmationOpen}
        onClickedCancel={cancelConfirmation}
        header={t('confirmCreateNewsletterTitle')}
        content={<p>{t('confirmCreateMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: handleNewsLetterAddition,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelConfirmation,
          },
        }}
        styleClasses="z-100"
      />

      <FormModelComponent
        isOpen={isNewsLetterModalOpen}
        header={t('createNewsletterTitle')}
        content={
          <AddNewsLetterForm
            form={addNewsLetterForm}
            handleCancel={handleCancel}
            getConfirmation={getConfirmation}
            t={t}
          />
        }
        onClickedCancel={closeNewsLetterModal}
        footerAction={{
          confimation: {
            label: t('createNewsLetter'),
            onClick: getConfirmation,
          },
          cancel: {
            label: t('cancel'),
            onClick: handleCancel,
          },
        }}
        closeIcon={<XSquare className="text-primary-font-red" size={20} />}
        contentStyles="mb-4 px-2 max-h-[80vh] overflow-y-auto space-y-2"
      ></FormModelComponent>

      <div className="mb-16 mt-12 flex w-full flex-col justify-start gap-4 md:flex-row md:items-center md:justify-between">
        <div className="items-start">
          <h1 className="text-[24px] text-headings-font-color sm:text-[36px]">
            {t('reportGeneratorTitle')}
          </h1>
        </div>

        {/* <div className="flex justify-end gap-4">
            <SecondarySquareBtn handleOnClick={() => {}}>
              <div className="flex">
                <Download className="text-blue mr-2 h-6 w-6" />
                <p className="pt-1">{t('downloadPDF')}</p>
              </div>
            </SecondarySquareBtn>
            <PrimarySquareBtn handleOnClick={() => {}}>
              <div className="flex">
                <Sliders2 className="mr-2 h-6 w-6 text-white" />
                <p className="pt-1">{t('generateReport')}</p>
              </div>
            </PrimarySquareBtn>
          </div> */}
      </div>

      {/* filter section */}
      <div className="my-6">
        <section className="mb-8 w-full">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">{t('filters')}</h3>
              <Button
                type="link"
                onClick={clearAllFilters}
                className="hover:text-primary-dark text-sm text-primary"
                disabled={!enableClearFiltersBtn}
              >
                {t('clearFilters')}
              </Button>
            </div>
            <Form form={newsletterFiltersForm} name="newsletterFilters">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <div className="mb-2">
                    <label className="text-sm font-medium text-gray-700">{t('basket')}</label>
                  </div>
                  <Select
                    className="w-full [&>.ant-select-selector]:!rounded-lg"
                    placeholder={t('basketsFilterPlaceholder')}
                    // mode="multiple"
                    allowClear
                    maxTagCount={2}
                    onClear={() => setSelectedBasket(undefined)}
                    value={selectedBasket}
                    onChange={setSelectedBasket}
                    loading={isLoadingFilterData}
                    options={baskets.map((basket: any) => ({
                      label: basket.name,
                      value: basket.id,
                    }))}
                    showSearch
                    filterOption={(input, option) =>
                      normalize(option?.label ?? '')
                        .toString()
                        .toLowerCase()
                        .includes(normalize(input.toLowerCase()))
                    }
                  />
                </Col>

                <Col xs={24} sm={12} md={8} lg={4}>
                  <div className="mb-2">
                    <label className="text-sm font-medium text-gray-700">{t('startDate')}</label>
                  </div>
                  <Form.Item
                    name="selectedStartDate"
                    valuePropName="value"
                    getValueFromEvent={(selectedStartDate) => selectedStartDate}
                    validateFirst
                    rules={[
                      {
                        validator: (_, value) => {
                          if (selectedEndDate && value && value.isAfter(selectedEndDate)) {
                            return Promise.reject(t('startDateAfterEndDateError'));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <DatePicker
                      className="w-full [&>.ant-picker]:!rounded-lg"
                      value={selectedStartDate}
                      onChange={setSelectedStartDate}
                      format="YYYY-MM-DD"
                      placeholder={t('startDatePlaceholder')}
                      disabledDate={disableFutureDates}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8} lg={4}>
                  <div className="mb-2">
                    <label className="text-sm font-medium text-gray-700">{t('endDate')}</label>
                  </div>
                  <Form.Item
                    name="selectedEndDate"
                    valuePropName="value"
                    getValueFromEvent={(selectedEndDate) => selectedEndDate}
                    dependencies={['selectedStartDate']}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (selectedStartDate && value && value.isBefore(selectedStartDate)) {
                            return Promise.reject(t('endDateBeforeStartDateError'));
                          }
                          return Promise.resolve();
                        },
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const start = getFieldValue('selectedStartDate');
                          if (!start || !value) return Promise.resolve();

                          const diff = value.diff(start, 'day');
                          if (diff >= 7) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(t('weekGapError')));
                        },
                      }),
                    ]}
                  >
                    <DatePicker
                      className="w-full [&>.ant-picker]:!rounded-lg"
                      value={selectedEndDate}
                      onChange={setSelectedEndDate}
                      format="YYYY-MM-DD"
                      placeholder={t('endDatePlaceholder')}
                      disabledDate={disableFutureDates}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8} lg={5}>
                  <div className="mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      {t('establishmentTypes')}
                    </label>
                  </div>
                  <Select
                    className="w-full [&>.ant-select-selector]:!rounded-lg"
                    placeholder={t('establishmentTypePlaceholder')}
                    mode="multiple"
                    onClear={() => setSelectedEstablishmentType(undefined)}
                    value={selectedEstablishmentType || undefined}
                    onChange={setSelectedEstablishmentType}
                    options={establishmentTypes}
                    loading={isLoadingFilterData}
                    showSearch
                    maxTagCount={1}
                    maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 15 : 10}
                    filterOption={(input, option) =>
                      normalize(option?.label ?? '')
                        .toString()
                        .toLowerCase()
                        .includes(normalize(input.toLowerCase()))
                    }
                  />
                  <div style={{ display: 'flex', marginBottom: '8px' }}>
                    {selectedEstablishmentType && selectedEstablishmentType?.length > 0 && (
                      <a
                        className="ml-auto"
                        onClick={() => {
                          setSelectedEstablishmentType([]);
                        }}
                      >
                        {t('basket:deselectAll')}
                      </a>
                    )}
                  </div>
                </Col>

                <Col xs={24} sm={12} md={8} lg={5}>
                  <div className="mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      {t('establishments')}
                    </label>
                  </div>
                  <Select
                    className="w-full [&>.ant-select-selector]:!rounded-lg"
                    placeholder={t('establishmentPlaceholder')}
                    mode="multiple"
                    onClear={() => setSelectedEstablishments([])}
                    value={selectedEstablishments || undefined}
                    onChange={setSelectedEstablishments}
                    disabled={!selectedEstablishmentType || establishments.length === 0}
                    options={establishments.map((establishment) => ({
                      label: establishment,
                      value: establishment,
                    }))}
                    loading={isLoadingFilterData}
                    maxTagCount={1}
                    maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 15 : 10}
                    showSearch
                    filterOption={(input, option) =>
                      normalize(option?.label ?? '')
                        .toString()
                        .toLowerCase()
                        .includes(normalize(input.toLowerCase()))
                    }
                  />
                  <div style={{ display: 'flex', marginBottom: '8px' }}>
                    {selectedEstablishments?.length > 0 && (
                      <a
                        className="ml-auto"
                        onClick={() => {
                          setSelectedEstablishments([]);
                        }}
                      >
                        {t('basket:deselectAll')}
                      </a>
                    )}
                  </div>
                </Col>
              </Row>
            </Form>

            <div className="mt-6 text-sm">{t('dateNote')}</div>
          </div>
        </section>

        <div className="mb-16 mt-12 flex w-full flex-col gap-4 md:flex-row md:items-end md:justify-end">
          <div className="flex justify-end gap-4">
            <SecondarySquareBtn handleOnClick={handleUpdateNewBtn}>
              <div className="flex p-1">
                <p>{t('cancel')}</p>
              </div>
            </SecondarySquareBtn>

            {/* <SecondarySquareBtn handleOnClick={() => {}} disabled={!enableDownloadBtn}>
                <div className="flex">
                  <Download className="text-blue mr-2 h-6 w-6" />
                  <p className="pt-1">{t('downloadPDF')}</p>
                </div>
              </SecondarySquareBtn> */}
            <PrimarySquareBtn
              handleOnClick={handleGenerateReport}
              disabled={!enableGenerateReportBtn}
            >
              <div className="flex">
                <Sliders2 className="mr-2 h-6 w-6 text-white" />
                <p className="pt-1">{t('generateNewsletter')}</p>
              </div>
            </PrimarySquareBtn>
          </div>
        </div>

        <div className="mt-6 w-full overflow-x-auto">
          {/* <TableComponent
                tableData={generatedData}
                pageSize={pageSize}
                currentPage={currentPage}
                totalItems={totalGeneratedData}
                onPaginationChanged={()=>{}}
                columns={getAlertConfigColumns()}
                loading={isLoadingGeneratedData}
                showHeader
                /> */}
        </div>

        {/* ---- Loader for table and graph ---- */}
        {(isLoadingGeneratedData || isLoadingGraphData) && (
          <div
            className="flex w-full flex-row items-center !justify-center"
            style={{
              // width: width || 500,
              height: 200,
            }}
          >
            <Spin size="large" />
          </div>
        )}

        {/* ---- Report table ---- */}
        <Form
          form={descriptionForm}
          onFieldsChange={(_, allFields) => {
            const hasErrors = allFields.some((field) => field.errors && field.errors.length > 0);
            setDisableSaveNewsletter(hasErrors);
          }}
        >
          {!isLoadingGeneratedData && generatedData?.length > 0 && (
            <>
              <NewsLetterTable
                data={generatedData}
                selectedStartDate={selectedStartDate}
                selectedEndDate={selectedEndDate}
                selectedEstablishments={selectedEstablishments}
              />

              <Form.Item
                label={t('mainDescription')}
                name="mainDescription"
                layout="vertical"
                rules={[
                  { required: false },
                  {
                    validator: (_, value) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('mainDescriptionSpaceValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      // Allow letters, numbers, space, punctuation and special characters
                      const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                      return regex.test(value)
                        ? Promise.resolve()
                        : Promise.reject(t('invalidInput'));
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength < 1) {
                        return Promise.reject(t('mainDescriptionMinValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength > 200) {
                        return Promise.reject(t('mainDescriptionMaxValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <TextArea placeholder={t('mainDescriptionPlaceholder')} rows={5} />
              </Form.Item>
            </>
          )}

          {/* ---- Graph table ---- */}

          {!isLoadingGraphData &&
            generatedGraphData?.length > 0 &&
            generatedGraphData?.map((establishmentData: any) => (
              <div key={establishmentData?.establishmentId}>
                {establishmentData?.priceVariance.length > 0 && (
                  <PriceVsVarianceChart
                    establishment={establishmentData?.establishment}
                    priceVarianceArr={establishmentData?.priceVariance}
                    establishmentId={establishmentData?.establishmentId}
                    descriptionFieldName={`establishment-desc-${establishmentData?.establishmentId}`}
                    mode="add"
                  />
                )}
              </div>
            ))}
        </Form>

        {!isLoadingGraphData &&
          generatedGraphData?.length > 0 &&
          generatedGraphData?.every(
            (establishmentData: any) => establishmentData?.priceVariance.length === 0
          ) && <p className="text-center">{t('noData')}</p>}

        {/* --- save newsletter --- */}
        {((!isLoadingGeneratedData && generatedData?.length > 0) ||
          (!isLoadingGraphData &&
            generatedGraphData?.some((item: any) => item.priceVariance.length > 0))) && (
          <div className="my-12 flex justify-end">
            <PrimaryButton handleOnClick={saveNewsLetterData} disabled={disableSaveNewsletter}>
              <div className="flex">
                <SaveOutlined className="mr-2 h-6 w-6 text-white" />
                <p className="pt-1">{t('saveNewsLetter')}</p>
              </div>
            </PrimaryButton>
          </div>
        )}
      </div>
    </>
  );
};

export default CustomNewsletterGeneration;
